import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../../shared/components/notifications/notification.service';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DeleteConfirmDialogComponent } from '../../../../shared/components/delete-confirm-dialog/delete-confirm-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { TodoService } from '../../../../core/services/todo.service';
import { SubTodoService } from '../../../../core/services/sub-todo.service';
import { TodoForListing } from '../../../../core/models/todos/todo-for-listing.model';
import { TodoRequestFilter, TodoStateFilterEnum } from '../../../../core/models/todos/todo-request-filter.model';
import { SubTodoForListing } from '../../../../core/models/subtodos/sub-todo-for-listing.model';
import { AddSubTodoDialogComponent, AddSubTodoDialogData } from '../add-subtodo-dialog/add-subtodo-dialog.component';

@Component({
  selector: 'app-todo-list',
  standalone: false,
  templateUrl: './todo-list.component.html',
  styleUrl: './todo-list.component.css'
})



export class TodoListComponent implements OnInit {
  readonly TodoStateFilterEnum = TodoStateFilterEnum;
  todos: TodoForListing[] = [];
  isLoading = false;

  currentPage = 0;
  pageSize = 10;
  totalItems = 0;

  searchTerm: string | undefined = undefined;
  /** State filter: All=1, Incomplete=2, Pending=3, Overdues=4. Default All. */
  stateFilter: TodoStateFilterEnum = TodoStateFilterEnum.All;

  constructor(
    private todoService: TodoService,
    private subTodoService: SubTodoService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    const savedSearch = sessionStorage.getItem('todos.list.search');
    const savedPage = sessionStorage.getItem('todos.list.page');
    const savedPageSize = sessionStorage.getItem('todos.list.pageSize');
    const savedState = sessionStorage.getItem('todos.list.state');

    this.searchTerm = savedSearch !== null ? (savedSearch || undefined) : this.searchTerm;
    this.currentPage = savedPage !== null ? parseInt(savedPage, 10) || 0 : this.currentPage;
    this.pageSize = savedPageSize !== null ? parseInt(savedPageSize, 10) || this.pageSize : this.pageSize;
    if (savedState !== null && savedState !== '') {
      const n = parseInt(savedState, 10);
      if (!isNaN(n) && n >= 1 && n <= 4) this.stateFilter = n as TodoStateFilterEnum;
    }

    this.loadTodos();
  }

  loadTodos(): void {
    this.isLoading = true;
    const apiPage = this.currentPage + 1;

    const filter: TodoRequestFilter = {
      page: apiPage,
      pageSize: this.pageSize,
      ...(this.searchTerm !== undefined && this.searchTerm !== '' && { search: this.searchTerm }),
      state: this.stateFilter
    };

    this.todoService.getPaginatedTodos(filter).subscribe({
      next: ({ data, pagination }) => {
        this.todos = data;
        this.totalItems = pagination.totalItems;
        this.pageSize = pagination.itemsPerPage;
        this.currentPage = pagination.currentPage - 1;
        this.isLoading = false;
      },
      error: () => {
        this.notificationService.showError('Failed to load todos');
        this.isLoading = false;
      }
    });
  }

  onSearchChange(): void {
    this.currentPage = 0;
    sessionStorage.setItem('todos.list.search', this.searchTerm || '');
    sessionStorage.setItem('todos.list.state', this.stateFilter.toString());
    sessionStorage.setItem('todos.list.page', this.currentPage.toString());
    sessionStorage.setItem('todos.list.pageSize', this.pageSize.toString());
    this.loadTodos();
  }

  onStateFilterChange(): void {
    this.currentPage = 0;
    sessionStorage.setItem('todos.list.search', this.searchTerm || '');
    sessionStorage.setItem('todos.list.state', this.stateFilter.toString());
    sessionStorage.setItem('todos.list.page', this.currentPage.toString());
    sessionStorage.setItem('todos.list.pageSize', this.pageSize.toString());
    this.loadTodos();
  }

  clearSearch(): void {
    this.searchTerm = undefined;
    this.currentPage = 0;
    sessionStorage.removeItem('todos.list.search');
    sessionStorage.setItem('todos.list.state', this.stateFilter.toString());
    sessionStorage.setItem('todos.list.page', this.currentPage.toString());
    sessionStorage.setItem('todos.list.pageSize', this.pageSize.toString());
    this.loadTodos();
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    sessionStorage.setItem('todos.list.search', this.searchTerm || '');
    sessionStorage.setItem('todos.list.state', this.stateFilter.toString());
    sessionStorage.setItem('todos.list.page', this.currentPage.toString());
    sessionStorage.setItem('todos.list.pageSize', this.pageSize.toString());
    this.loadTodos();
  }

  deleteTodo(id: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { itemName: 'this todo' }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.todoService.softDeleteTodo(id).subscribe({
          next: () => {
            this.notificationService.showSuccess('Todo deleted successfully');
            this.loadTodos();
          },
          error: () => this.notificationService.showError('Failed to delete todo')
        });
      }
    });
  }

  isTodoOverdue(todo: TodoForListing): boolean {
    if (!todo.dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(todo.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today && !todo.isCompleted;
  }

  checkSubTodo(subTodo: SubTodoForListing, todo: TodoForListing): void {
    this.subTodoService.checkSubTodo(subTodo.id).subscribe({
      next: () => {
        subTodo.isDone = true;
        this.snackBar.open('Sub-todo checked', 'Close', { duration: 2000 });
      },
      error: () => {
        this.snackBar.open('Failed to check sub-todo', 'Close', { duration: 3000 });
      }
    });
  }

  uncheckSubTodo(subTodo: SubTodoForListing, todo: TodoForListing): void {
    this.subTodoService.uncheckSubTodo(subTodo.id).subscribe({
      next: () => {
        subTodo.isDone = false;
        this.snackBar.open('Sub-todo unchecked', 'Close', { duration: 2000 });
      },
      error: () => {
        this.snackBar.open('Failed to uncheck sub-todo', 'Close', { duration: 3000 });
      }
    });
  }

  getSubTodoColor(subTodo: SubTodoForListing, todo: TodoForListing): string {
    if (subTodo.isDone) {
      return '#4caf50'; // Green for checked
    } else if (!todo.isCompleted) {
      return '#f44336'; // Red for unchecked when todo is not completed
    } else if (this.isTodoOverdue(todo)) {
      return '#f44336'; // Red for overdue unchecked
    } else {
      return '#9e9e9e'; // Gray for unchecked
    }
  }

  deleteSubTodo(subTodoId: number, todoId: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { itemName: 'this sub-todo' }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.subTodoService.deleteSubTodo(subTodoId).subscribe({
          next: () => {
            this.notificationService.showSuccess('Sub-todo deleted successfully');
            // Reload todos to refresh the list
            this.loadTodos();
          },
          error: () => {
            this.notificationService.showError('Failed to delete sub-todo');
          }
        });
      }
    });
  }

  openAddSubTodoDialog(todoId: number): void {
    const dialogData: AddSubTodoDialogData = {
      todoId: todoId
    };

    const dialogRef = this.dialog.open(AddSubTodoDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload todos to refresh the list
        this.loadTodos();
      }
    });
  }

  markTodoAsCompleted(todo: TodoForListing): void {
    const dialogData: ConfirmDialogData = {
      title: 'Mark as Completed',
      message: 'Are you sure you want to mark this todo as completed?',
      confirmText: 'Yes',
      cancelText: 'No'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.todoService.markTodoAsCompleted(todo.id).subscribe({
          next: () => {
            this.snackBar.open('Todo marked as completed', 'Close', { duration: 2000 });
            this.loadTodos();
          },
          error: () => {
            this.snackBar.open('Failed to mark todo as completed', 'Close', { duration: 3000 });
          }
        });
      }
    });
  }

  unmarkTodoAsCompleted(todo: TodoForListing): void {
    const dialogData: ConfirmDialogData = {
      title: 'Mark as Not Completed',
      message: 'Are you sure you want to mark this todo as not completed?',
      confirmText: 'Yes',
      cancelText: 'No'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.todoService.unmarkTodoAsCompleted(todo.id).subscribe({
          next: () => {
            this.snackBar.open('Todo marked as not completed', 'Close', { duration: 2000 });
            this.loadTodos();
          },
          error: () => {
            this.snackBar.open('Failed to mark todo as not completed', 'Close', { duration: 3000 });
          }
        });
      }
    });
  }
}
